import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Search, Filter, FileDown, Download, Clock, ExternalLink,
  File, Image, FileSpreadsheet, Film, Archive, HardDrive
} from 'lucide-react';
import client from '../api/client';
import { adminApi } from '../api/admin';

const PAGE_SIZE = 30;

const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: 'text-red-500' },
  'image/': { icon: Image, color: 'text-blue-500' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml': { icon: FileText, color: 'text-blue-600' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml': { icon: FileSpreadsheet, color: 'text-green-600' },
  'video/': { icon: Film, color: 'text-purple-500' },
  'application/zip': { icon: Archive, color: 'text-yellow-600' },
};

function getFileIcon(mime) {
  if (!mime) return { icon: File, color: 'text-gray-400' };
  for (const [key, val] of Object.entries(FILE_ICONS)) {
    if (mime.startsWith(key)) return val;
  }
  return { icon: File, color: 'text-gray-400' };
}

function formatSize(bytes) {
  if (!bytes) return '–';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function formatTotalSize(bytes) {
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1073741824).toFixed(2) + ' GB';
}

const SOURCE_LABELS = { upload: 'Uploaded', generated: 'Generated', portal: 'Portal Upload' };
const SOURCE_COLORS = { upload: 'bg-blue-50 text-blue-600', generated: 'bg-green-50 text-green-600', portal: 'bg-purple-50 text-purple-600' };

export default function DocumentsAudit() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [category, setCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recordTypeId, setRecordTypeId] = useState('');
  const [recordTypes, setRecordTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    adminApi.getRecordTypes().then(r => setRecordTypes(r.data.record_types || [])).catch(() => {});
    loadStats();
  }, []);

  useEffect(() => { loadFiles(); }, [page, source]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (search) params.search = search;
      if (source) params.source = source;
      if (category) params.category = category;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (recordTypeId) params.record_type_id = recordTypeId;
      const { data } = await client.get('/documents-audit', { params });
      setFiles(data.files || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await client.get('/documents-audit/stats');
      setStats(data);
    } catch (err) {}
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (source) params.set('source', source);
      if (category) params.set('category', category);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (recordTypeId) params.set('record_type_id', recordTypeId);
      const { data } = await client.get(`/documents-audit/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `documents_audit_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const handleDownload = async (file) => {
    try {
      const { data } = await client.get(`/documents/${file.id}/download`);
      window.open(data.url, '_blank');
    } catch (err) { console.error(err); }
  };

  const formatDate = (ts) => new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">📄 Documents Audit</h1>
          <p className="text-sm text-gray-500">All documents across records — upload, generation, and access history</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Filter className="w-4 h-4" /> Filters
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800">
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[
          { label: 'Total Files', value: stats.total_files || 0 },
          { label: 'Total Size', value: formatTotalSize(stats.total_size || 0) },
          { label: 'Uploaded', value: stats.uploaded || 0 },
          { label: 'Generated', value: stats.generated || 0 },
          { label: 'Last 7 Days', value: stats.last_7_days || 0 },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">{s.label}</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadFiles()}
                  placeholder="Filename, description..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Record Type</label>
              <select value={recordTypeId} onChange={e => setRecordTypeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="">All</option>
                {recordTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setRecordTypeId(''); setCategory(''); setSource(''); setPage(0); }}
              className="px-3 py-1.5 text-xs text-gray-500">Clear</button>
            <button onClick={() => { setPage(0); loadFiles(); }}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg">Apply</button>
          </div>
        </div>
      )}

      {/* Source Filters */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        {[
          { value: '', label: 'All' },
          { value: 'upload', label: 'Uploaded' },
          { value: 'generated', label: 'Generated' },
          { value: 'portal', label: 'Portal' },
        ].map(f => (
          <button key={f.value} onClick={() => { setSource(f.value); setPage(0); }}
            className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap transition ${
              source === f.value ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : files.length === 0 ? (
          <div className="text-center py-12">
            <HardDrive className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400">No documents found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">File</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Record</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Source</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Category</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">By</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Size</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {files.map(file => {
                const { icon: FileIcon, color: iconColor } = getFileIcon(file.mime_type);
                return (
                  <tr key={file.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileIcon className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate max-w-[200px]">{file.original_name}</p>
                          {file.description && <p className="text-[10px] text-gray-400 truncate max-w-[200px]">{file.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {file.record_id && (
                        <button onClick={() => navigate(`/records/${file.record_type_slug}/${file.record_id}`)}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          {file.reference_number || file.record_title || 'View'}
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                      {file.record_type_name && <span className="text-[10px] text-gray-400 block">{file.record_type_name}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SOURCE_COLORS[file.source] || 'bg-gray-100 text-gray-500'}`}>
                        {SOURCE_LABELS[file.source] || file.source || 'Upload'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{file.category || '–'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {file.uploader_first_name ? file.uploader_first_name + ' ' + (file.uploader_last_name || '') : '–'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(file.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-400">{formatSize(file.size_bytes)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDownload(file)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
